import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const FIELD_CLASSES = 'tw-min-w-0 tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-text-sm tw-py-2 tw-px-3';

const KIND_CHILD = 'child';

const round2 = (value) => Math.round(value * 100) / 100;

const format = (template, values) => Object.keys(values).reduce((text, key) => text.split(`:${key}`).join(values[key]), template || '');

const sameId = (a, b) => String(a) === String(b);

/**
 * Budget tags editor: inline on the expense edit page, or inside an item's tags modal,
 * which passes `onChange` to collect the rows instead of posting them with a form.
 *
 * A row whose item is no longer taggable keeps that item as an extra option in
 * its own row only, so it can be edited or removed but never re-added.
 * Rows of a line that follows its project are ignored by the server, so while
 * the override is off they are disabled: not posted and unable to block the submit.
 */
export default function BudgetTagsSection({ config, i18n, disabled, onChange, idPrefix = 'budget-tags' }) {
    const codesets = useMemo(() => config.codesets || [], [config]);
    const codesetsById = useMemo(() => Object.fromEntries(codesets.map((codeset) => [String(codeset.id), codeset])), [codesets]);
    const isChild = config.kind === KIND_CHILD;

    const keyCounter = useRef(0);
    const withKey = (row) => ({ ...row, retained_category_id: row.category_id, key: keyCounter.current++ });

    const [rows, setRows] = useState(() => (config.rows || []).map(withKey));
    const [overriding, setOverriding] = useState(!!config.overrides);

    const editorActive = !disabled && (!isChild || overriding);
    const overrideId = `${idPrefix}-override`;
    const inheritedGroups = config.inherited_groups || [{ id: 'parent', name: config.project_name, tags: config.inherited || [] }];

    useEffect(() => {
        if (onChange) {
            onChange({
                overrides: overriding,
                rows: rows.map((row) => ({ tag_id: row.tag_id, percentage: row.percentage })),
            });
        }
    }, [rows, overriding]);

    const freeItemId = (codeset, otherRows) => {
        const used = otherRows.map((row) => String(row.tag_id));
        const free = codeset.items.filter((item) => !used.includes(String(item.id)));

        return (free[0] || codeset.items[0] || {}).id;
    };

    const addRow = () => {
        const codeset = codesets[0];
        if (!codeset) return;
        setRows((current) => [...current, withKey({ category_id: codeset.id, tag_id: freeItemId(codeset, current), percentage: 100 })]);
    };

    const removeRow = (key) => setRows((current) => current.filter((row) => row.key !== key));

    const updateRow = (key, changes) => setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

    const changeCodeset = (key, categoryId) => {
        const codeset = codesetsById[String(categoryId)];
        setRows((current) =>
            current.map((row) => {
                if (row.key !== key) return row;
                const others = current.filter((other) => other.key !== key);

                return { ...row, category_id: categoryId, tag_id: codeset ? freeItemId(codeset, others) : null };
            }),
        );
    };

    const changeOverride = (checked) => {
        if (checked && rows.length === 0) {
            setRows((config.inherited || []).filter((row) => !row.retained).map(withKey));
        }
        setOverriding(checked);
    };

    const usedIds = rows.map((row) => String(row.tag_id));

    const summary = [];
    const summaryByCodeset = {};
    rows.forEach((row) => {
        const key = String(row.category_id);
        if (!summaryByCodeset[key]) {
            summaryByCodeset[key] = { name: codesetsById[key]?.name ?? row.category_name, total: 0 };
            summary.push(summaryByCodeset[key]);
        }
        summaryByCodeset[key].total += parseFloat(row.percentage) || 0;
    });

    const renderSummaryPart = (entry) => {
        const total = round2(entry.total);
        if (Math.abs(total - 100) < 0.005) {
            return <span className="tw-text-green tw-font-semibold">{format(i18n.complete, { codeset: entry.name, total })}</span>;
        }
        if (total > 100) {
            return <span className="tw-text-red tw-font-semibold">{format(i18n.over, { codeset: entry.name, total })}</span>;
        }

        return <span className="tw-text-charcoal-75">{format(i18n.partial, { codeset: entry.name, tagged: total, untagged: round2(100 - total) })}</span>;
    };

    const renderRow = (row, index) => {
        const codeset = codesetsById[String(row.category_id)];
        const items = codeset ? codeset.items : [];
        const retainedHere = row.retained && sameId(row.retained_category_id, row.category_id);
        const retainedLabel = row.retained ? `${row.item_name} ${i18n.noLongerTaggable}` : null;
        const selectedItem = items.find((item) => sameId(item.id, row.tag_id));
        const itemTitle = selectedItem ? selectedItem.name : retainedLabel || '';
        const codesetTitle = codeset ? codeset.name : row.category_name;

        return (
            <div key={row.key} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <select
                    className={`tw-flex-1 ${FIELD_CLASSES}`}
                    value={String(row.category_id)}
                    title={codesetTitle}
                    disabled={!editorActive}
                    onChange={(event) => changeCodeset(row.key, event.target.value)}
                >
                    {codesets.map((option) => (
                        <option key={option.id} value={String(option.id)}>
                            {option.name}
                        </option>
                    ))}
                    {row.retained && !codeset && <option value={String(row.category_id)}>{row.category_name}</option>}
                </select>
                <select
                    className={`tw-flex-1 ${FIELD_CLASSES}`}
                    name={`budget_tags[${row.key}][tag_id]`}
                    value={String(row.tag_id)}
                    title={itemTitle}
                    disabled={!editorActive}
                    onChange={(event) => updateRow(row.key, { tag_id: event.target.value })}
                >
                    {items.map((item) => (
                        <option key={item.id} value={String(item.id)} disabled={!sameId(item.id, row.tag_id) && usedIds.includes(String(item.id))}>
                            {item.name}
                        </option>
                    ))}
                    {retainedHere && <option value={String(row.tag_id)}>{retainedLabel}</option>}
                </select>
                <input
                    type="number"
                    className={`tw-w-[72px] tw-text-end ${FIELD_CLASSES}`}
                    name={`budget_tags[${row.key}][percentage]`}
                    min="0.01"
                    max="100"
                    step="0.01"
                    value={row.percentage ?? ''}
                    disabled={!editorActive}
                    onChange={(event) => updateRow(row.key, { percentage: event.target.value })}
                />
                {!disabled && (
                    <button
                        type="button"
                        className="tw-p-0 tw-bg-transparent tw-border-0 tw-text-red tw-cursor-pointer tw-text-xl tw-leading-none"
                        aria-label={i18n.remove}
                        onClick={() => removeRow(row.key)}
                    >
                        &times;
                    </button>
                )}
                {retainedLabel && <div className="tw-w-full tw-text-xs tw-italic tw-text-charcoal-75">{retainedLabel}</div>}
            </div>
        );
    };

    const [inheritedBefore, inheritedAfter] = (i18n.inheritedFrom || '').split(':project');

    return (
        <>
            {isChild && (
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-border tw-border-dashed tw-border-charcoal-25 tw-rounded-lg tw-p-3 tw-mb-2">
                    <div className="tw-space-y-1">
                        {inheritedGroups.length === 0 && <div className="tw-text-sm tw-text-charcoal-75">{i18n.noProjectTags}</div>}
                        {inheritedGroups.map((group) => (
                            <div key={group.id} className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-sm tw-text-charcoal-75">
                                <span className="tw-me-1">
                                    {inheritedBefore}
                                    <b>{group.name}</b>
                                    {inheritedAfter}
                                </span>
                                {(group.tags || []).length > 0 ? (
                                    group.tags.map((tag) => (
                                        <span
                                            key={tag.tag_id}
                                            className="tw-border tw-border-dashed tw-border-charcoal-50 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs"
                                        >
                                            {tag.item_name} {parseFloat(tag.percentage)}%
                                        </span>
                                    ))
                                ) : (
                                    <span>{i18n.noProjectTags}</span>
                                )}
                            </div>
                        ))}
                    </div>
                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-shrink-0 tw-mb-0" htmlFor={overrideId}>
                        <input type="hidden" name="overrides_budget_tags" value="0" disabled={disabled} />
                        <input
                            type="checkbox"
                            id={overrideId}
                            name="overrides_budget_tags"
                            value="1"
                            checked={overriding}
                            disabled={disabled}
                            onChange={(event) => changeOverride(event.target.checked)}
                        />
                        {i18n.overrideLabel}
                    </label>
                </div>
            )}

            <div className={isChild && !overriding ? 'tw-hidden' : ''}>
                <div className="tw-space-y-2 tw-mb-2">{rows.map(renderRow)}</div>

                {!disabled && codesets.length > 0 && (
                    <button type="button" className="tw-p-0 tw-bg-transparent tw-border-0 tw-text-blue tw-cursor-pointer hover:tw-underline" onClick={addRow}>
                        + {i18n.addTag}
                    </button>
                )}

                <p className="tw-text-sm tw-mt-1 tw-mb-0">
                    {summary.length === 0 ? (
                        <span className="tw-text-charcoal-75">{i18n.noTags}</span>
                    ) : (
                        summary.map((entry, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <span className="tw-text-charcoal-75"> · </span>}
                                {renderSummaryPart(entry)}
                            </React.Fragment>
                        ))
                    )}
                </p>

                {isChild && <p className="tw-text-charcoal-75 tw-text-sm tw-italic tw-mt-1 tw-mb-0">{i18n.hint}</p>}
            </div>

            {!isChild && <p className="tw-text-charcoal-75 tw-text-sm tw-italic tw-mt-1 tw-mb-0">{i18n.hint}</p>}
        </>
    );
}

function parseJson(raw, fallback) {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return fallback;
    }
}

const rootEl = document.getElementById('budget-tags-section');
if (rootEl) {
    ReactDOM.render(
        <BudgetTagsSection
            config={parseJson(rootEl.getAttribute('data-config'), {})}
            i18n={parseJson(rootEl.getAttribute('data-i18n'), {})}
            disabled={rootEl.getAttribute('data-disabled') === '1'}
        />,
        rootEl,
    );
}
